import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, TruckIcon, DocumentArrowDownIcon, ArrowTopRightOnSquareIcon, ScaleIcon, CalendarDaysIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ShipmentCardBlock, getStatusLabel, getShipmentStatusLabel, formatUtcToLocalDateOnly } from '../../types';
import { Badge } from '../../../../components/ui/badge';
import { cn } from '../../../../lib/utils';

interface ShipmentCardRendererProps {
  block: ShipmentCardBlock;
}

export const ShipmentCardRenderer: React.FC<ShipmentCardRendererProps> = ({ block }) => {
  const {
    cn_number,
    origin,
    destination,
    status,
    shipment_status,
    cn_date,
    origin_pincode,
    destination_pincode,
    distance_km,
    edd,
    delivery_date,
    pod_upload_date,
    actual_weight,
    charge_weight,
    load_type,
    consignor,
    consignee,
    pod_link,
    hold_info,
    invoice_numbers,
    ewaybill_numbers,
    remaining_km,
  } = block;

  const isHold = shipment_status?.toLowerCase() === 'on_hold';
  const isDelivered = status?.toLowerCase() === 'delivered' || Boolean(delivery_date);
  const isLocal = status?.toLowerCase() === 'local';
  const canTrack = !isDelivered && !isLocal;

  return (
    <div className="my-3 w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TruckIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <span className="font-semibold text-gray-900 text-sm">CN: {cn_number}</span>
          {load_type && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{load_type}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {status && (
            <Badge
              variant="outline"
              className={cn('text-xs font-medium border', isDelivered ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200')}
            >
              {getStatusLabel(status)}
            </Badge>
          )}
          {shipment_status && (
            <Badge
              variant="outline"
              className={cn('text-xs font-medium border', isHold ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-gray-100 text-gray-700 border-gray-200')}
            >
              {getShipmentStatusLabel(shipment_status)}
            </Badge>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1.5 font-medium text-gray-800">
            <MapPinIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span>{origin}{origin_pincode ? ` (${origin_pincode})` : ''}</span>
          </div>
          <span className="text-gray-400">→</span>
          <div className="flex items-center gap-1.5 font-medium text-gray-800">
            <MapPinIcon className="w-4 h-4 text-rose-600 flex-shrink-0" />
            <span>{destination}{destination_pincode ? ` (${destination_pincode})` : ''}</span>
          </div>
          {((typeof remaining_km === 'number' && remaining_km > 0) || (typeof distance_km === 'number' && distance_km > 0)) && (
            <span className="text-xs text-gray-500 ml-auto">
              {typeof remaining_km === 'number' && remaining_km > 0 ? `${remaining_km.toLocaleString()} km remaining` : ''}
              {typeof remaining_km === 'number' && remaining_km > 0 && typeof distance_km === 'number' && distance_km > 0 ? ' / ' : ''}
              {typeof distance_km === 'number' && distance_km > 0 ? `${distance_km.toLocaleString()} km total` : ''}
            </span>
          )}
        </div>

        {isHold && hold_info && (
          <div className="p-2.5 bg-rose-50 border border-rose-100 rounded-lg text-xs text-rose-700 flex items-start gap-2">
            <ExclamationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-500" />
            <div className="space-y-0.5 min-w-0 flex-1">
              <div className="font-semibold text-rose-800">Hold Details</div>
              {typeof hold_info === 'object' && hold_info !== null ? (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-rose-700">
                  {hold_info.hold_reason && (
                    <span><span className="font-medium text-rose-800">Reason:</span> {hold_info.hold_reason}</span>
                  )}
                  {hold_info.hold_date && (
                    <span><span className="font-medium text-rose-800">Date:</span> {formatUtcToLocalDateOnly(hold_info.hold_date)}</span>
                  )}
                  {hold_info.hold_branch && (
                    <span><span className="font-medium text-rose-800">Branch:</span> {hold_info.hold_branch}</span>
                  )}
                </div>
              ) : (
                <div>{String(hold_info)}</div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1 text-xs">
          {cn_date && (
            <div className="bg-gray-50/70 p-2 rounded-lg">
              <span className="text-gray-500 block">Booking Date</span>
              <span className="font-medium text-gray-800 flex items-center gap-1 mt-0.5">
                <CalendarDaysIcon className="w-3.5 h-3.5 text-gray-400" />
                {formatUtcToLocalDateOnly(cn_date)}
              </span>
            </div>
          )}

          {edd && (
            <div className="bg-gray-50/70 p-2 rounded-lg">
              <span className="text-gray-500 block">Expected Delivery</span>
              <span className="font-medium text-gray-800 flex items-center gap-1 mt-0.5">
                <CalendarDaysIcon className="w-3.5 h-3.5 text-gray-400" />
                {formatUtcToLocalDateOnly(edd)}
              </span>
            </div>
          )}

          {delivery_date && (
            <div className="bg-emerald-50/50 p-2 rounded-lg">
              <span className="text-emerald-700 block">Delivered On</span>
              <span className="font-medium text-emerald-900 flex items-center gap-1 mt-0.5">
                <CalendarDaysIcon className="w-3.5 h-3.5 text-emerald-600" />
                {formatUtcToLocalDateOnly(delivery_date)}
              </span>
            </div>
          )}

          {pod_upload_date && (
            <div className="bg-emerald-50/50 p-2 rounded-lg">
              <span className="text-emerald-700 block">POD Uploaded</span>
              <span className="font-medium text-emerald-900 flex items-center gap-1 mt-0.5">
                <CalendarDaysIcon className="w-3.5 h-3.5 text-emerald-600" />
                {formatUtcToLocalDateOnly(pod_upload_date)}
              </span>
            </div>
          )}

          {(actual_weight || charge_weight) && (
            <div className="bg-gray-50/70 p-2 rounded-lg">
              <span className="text-gray-500 block">Weight</span>
              <span className="font-medium text-gray-800 flex items-center gap-1 mt-0.5">
                <ScaleIcon className="w-3.5 h-3.5 text-gray-400" />
                {actual_weight ? `${actual_weight} kg` : ''}
                {charge_weight ? ` (${charge_weight} kg chg)` : ''}
              </span>
            </div>
          )}

          {consignor && (
            <div className="bg-gray-50/70 p-2 rounded-lg">
              <span className="text-gray-500 block">Consignor</span>
              <span className="font-medium text-gray-800 truncate block mt-0.5" title={consignor}>
                {consignor}
              </span>
            </div>
          )}

          {consignee && (
            <div className="bg-gray-50/70 p-2 rounded-lg">
              <span className="text-gray-500 block">Consignee</span>
              <span className="font-medium text-gray-800 truncate block mt-0.5" title={consignee}>
                {consignee}
              </span>
            </div>
          )}

          {invoice_numbers && invoice_numbers.length > 0 && (
            <div className="bg-gray-50/70 p-2 rounded-lg">
              <span className="text-gray-500 block">Invoice(s)</span>
              <span className="font-medium text-gray-800 truncate block mt-0.5" title={invoice_numbers.join(', ')}>
                {invoice_numbers.join(', ')}
              </span>
            </div>
          )}

          {ewaybill_numbers && ewaybill_numbers.length > 0 && (
            <div className="bg-gray-50/70 p-2 rounded-lg">
              <span className="text-gray-500 block">E-Waybill(s)</span>
              <span className="font-medium text-gray-800 truncate block mt-0.5" title={ewaybill_numbers.join(', ')}>
                {ewaybill_numbers.join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {(canTrack || pod_link) && (
        <div className="px-4 py-2.5 bg-gray-50/70 border-t border-gray-100 flex items-center justify-end gap-2 text-xs">
          {pod_link && (
            <a
              href={pod_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
            >
              <DocumentArrowDownIcon className="w-4 h-4 text-gray-500" />
              <span>View POD</span>
              <ArrowTopRightOnSquareIcon className="w-3 h-3 text-gray-400" />
            </a>
          )}

          {canTrack && (
            <Link
              to={`/shipments/${cn_number}/track`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <TruckIcon className="w-4 h-4" />
              <span>Track shipment</span>
              <ArrowTopRightOnSquareIcon className="w-3 h-3 text-blue-200" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};
